import React, { useEffect } from 'react';
import Navbar from '../components/Navbar';
import { BookOpen, Award, Globe, Users, Target, Lightbulb } from 'lucide-react';

function Standard() {
  useEffect(() => {
    // Disable right-click
    const handleContextMenu = (e) => {
      e.preventDefault();
      return false;
    };

    // Disable certain keyboard shortcuts
    const handleKeyDown = (e) => {
      if (
        e.key === 'F12' ||
        (e.ctrlKey && e.shiftKey && (e.key === 'I' || e.key === 'J')) ||
        (e.ctrlKey && e.key === 'U')
      ) {
        e.preventDefault();
        return false;
      }
    };

    // Disable text selection and copy
    const handleCopy = (e) => {
      e.preventDefault();
      return false;
    };

    // Disable drag
    const handleDragStart = (e) => {
      e.preventDefault();
      return false;
    };

    // Add event listeners
    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('copy', handleCopy);
    document.addEventListener('dragstart', handleDragStart);

    // Disable text selection via CSS
    document.body.style.userSelect = 'none';
    document.body.style.webkitUserSelect = 'none';
    document.body.style.msUserSelect = 'none';

    // Cleanup
    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('copy', handleCopy);
      document.removeEventListener('dragstart', handleDragStart);
      document.body.style.userSelect = '';
      document.body.style.webkitUserSelect = '';
      document.body.style.msUserSelect = '';
    };
  }, []);

  return (
    <div className="bg-black text-white overflow-x-hidden w-full">
      {/* Navbar */}
      <Navbar />

      {/* Protection styles for images */}
      <style jsx>{`
        img {
          pointer-events: none;
          user-select: none;
          -webkit-user-drag: none;
          -webkit-user-select: none;
          -moz-user-select: none;
          -ms-user-select: none;
        }
        html, body {
          overflow-x: hidden;
          max-width: 100%;
        }
      `}</style>

      {/* Background stars effect */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        {[...Array(80)].map((_, i) => (
          <div
            key={i}
            className="absolute bg-white rounded-full opacity-50"
            style={{
              width: Math.random() * 2 + 1 + 'px',
              height: Math.random() * 2 + 1 + 'px',
              top: Math.random() * 100 + '%',
              left: Math.random() * 100 + '%',
            }}
          />
        ))}
      </div>

      {/* Hero Section */}
      <section className="relative px-4 sm:px-8 lg:px-24 py-12 sm:py-20 pt-24 sm:pt-32">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
            
            {/* Left Content */}
            <div className="space-y-6 sm:space-y-8 z-10">
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight">
                ISF Athena: Excellence in Education Aligned with National and Global Standards
              </h1>
              
              <p className="text-gray-400 text-base sm:text-lg leading-relaxed">
                ISF Athena stands at the forefront of educational excellence, seamlessly integrating India's National Education Policy 2020 with internationally recognized NAAC standards. Our commitment to STEM innovation and Personalized Adaptive Learning creates a transformative educational experience that prepares students for global leadership.
              </p>

              <div className="flex flex-wrap gap-3 sm:gap-4">
                <button className="px-6 sm:px-8 py-3 bg-orange-500 text-white rounded-xl font-medium hover:bg-orange-600 transition">
                  Explore Our Programs
                </button>
                <button className="px-6 sm:px-8 py-3 bg-transparent text-white rounded-xl font-medium hover:bg-white hover:text-black transition border-2 border-white">
                  Schedule a Visit
                </button>
              </div>
            </div>

            {/* Right Image */}
            <div className="relative z-10 overflow-hidden rounded-3xl">
            <div className="relative rounded-3xl overflow-hidden shadow-2xl shadow-purple-500/30 h-96">
                <img 
                src="/Assests/home_page_10.png" 
                alt="Modern Education Campus" 
                className="w-full h-full object-cover"
                />
            </div>
            
            {/* Floating glow effects */}
            <div className="absolute top-0 right-0 w-40 h-40 bg-purple-500/30 rounded-full blur-3xl animate-pulse" />
            <div className="absolute bottom-0 left-0 w-40 h-40 bg-blue-500/30 rounded-full blur-3xl animate-pulse" style={{animationDelay: '1s'}} />
            </div>
            
          </div>
        </div>
      </section>

      {/* Chapter 1 Section */}
      <section className="relative px-4 sm:px-8 lg:px-24 py-12 sm:py-20">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
            
            {/* Left Content */}
            <div className="space-y-6 sm:space-y-8 z-10">
              
              <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-orange-500">
                Commitment to National Education Policy and NAAC Standards
              </h3>
              
              <p className="text-gray-400 text-base sm:text-lg leading-relaxed">
                At ISF Athena, we don't just meet standards—we set them. Our framework harmonizes the visionary goals of India's National Education Policy with the rigorous benchmarks of NAAC accreditation, creating an ecosystem where academic excellence meets cultural values and innovative pedagogy.
              </p>
            </div>

            {/* Right Image */}
            <div className="relative z-10 overflow-hidden">
                <div className="relative z-10">
                    <div className="relative rounded-3xl overflow-hidden shadow-2xl shadow-blue-500/30 h-96">
                        <img 
                        src="/Assests/home_page_11.png" 
                        alt="Interactive Learning Environment" 
                        className="w-full h-full object-cover"
                        />
                    </div>
                    
                    {/* Floating glow effects */}
                    <div className="absolute top-0 right-0 w-32 h-32 bg-blue-500/30 rounded-full blur-3xl animate-pulse" />
                    <div className="absolute bottom-0 left-0 w-32 h-32 bg-cyan-500/30 rounded-full blur-3xl animate-pulse" style={{animationDelay: '1.5s'}} />
                </div>
              
              {/* Floating glow effects */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-blue-500/30 rounded-full blur-3xl animate-pulse" />
              <div className="absolute bottom-0 left-0 w-32 h-32 bg-cyan-500/30 rounded-full blur-3xl animate-pulse" style={{animationDelay: '1.5s'}} />
            </div>
            
          </div>
        </div>
      </section>

      {/* Upholding National Education Policy (NEP) 2020 Section */}
      <section className="relative px-4 sm:px-8 lg:px-24 py-12 sm:py-20">
        <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
            
            {/* Left Image */}
            <div className="relative z-10">
                <div className="relative rounded-3xl overflow-hidden shadow-2xl shadow-blue-500/30 h-750px]">
                <img 
                    src="/Assests/home_page_12.png" 
                    alt="NEP 2020 Education" 
                    className="w-full h-full object-cover"
                />
                </div>
                
                {/* Floating glow effects */}
                <div className="absolute top-0 right-0 w-40 h-40 bg-blue-500/30 rounded-full blur-3xl animate-pulse" />
                <div className="absolute bottom-0 left-0 w-40 h-40 bg-purple-500/30 rounded-full blur-3xl animate-pulse" style={{animationDelay: '1.5s'}} />
            </div>

            {/* Right Content */}
            <div className="space-y-8 z-10">
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white">
                Upholding National Education Policy (NEP) 2020
                </h2>

                {/* Cards Grid */}
                <div className="grid gap-6">
                
                {/* Top Row - 2 Cards */}
                <div className="grid md:grid-cols-2 gap-6">
                    
                    {/* Holistic Education Card */}
                    <div className="bg-black border-2 border-gray-700 rounded-2xl p-6 hover:border-orange-500 transition">
                    <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center mb-4">
                        <BookOpen className="w-6 h-6 text-white" />
                    </div>
                    <h4 className="text-xl font-bold text-white mb-3">
                        Holistic Education
                    </h4>
                    <p className="text-gray-400 text-sm leading-relaxed">
                        ISF Athena integrates NEP's transformative vision of holistic, multidisciplinary education that develops the whole child—intellectually, emotionally, and socially.
                    </p>
                    </div>

                    {/* Cultural Values Card */}
                    <div className="bg-black border-2 border-gray-700 rounded-2xl p-6 hover:border-orange-500 transition">
                    <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center mb-4">
                        <Globe className="w-6 h-6 text-white" />
                    </div>
                    <h4 className="text-xl font-bold text-white mb-3">
                        Cultural Values
                    </h4>
                    <p className="text-gray-400 text-sm leading-relaxed">
                        Strong emphasis on bilingual proficiency and cultural values aligns perfectly with NEP's dual focus on preserving Indian heritage while fostering a progressive global outlook.
                    </p>
                    </div>

                </div>

                {/* Bottom Row - 1 Full Width Card */}
                <div className="bg-black border-2 border-gray-700 rounded-2xl p-6 hover:border-orange-500 transition">
                    <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center mb-4">
                    <Lightbulb className="w-6 h-6 text-white" />
                    </div>
                    <h4 className="text-xl font-bold text-white mb-3">
                    Lifelong Learning
                    </h4>
                    <p className="text-gray-400 text-sm leading-relaxed">
                    Our curriculum is meticulously designed to foster critical thinking, boundless creativity, and the essential skills for lifelong learning in an ever-evolving world.
                    </p>
                </div>

                </div>
            </div>

            </div>
        </div>
        </section>

        {/* NAAC Accreditation and Quality Assurance Section */}
        <section className="relative px-4 sm:px-8 lg:px-24 py-12 sm:py-20">
        <div className="max-w-7xl mx-auto">
            
            {/* Main Heading */}
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-10 sm:mb-16 text-center">
            NAAC Accreditation and Quality Assurance
            </h2>

            <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-start">
            
            {/* Left Content */}
            <div className="space-y-12">
                
                {/* Excellence Framework */}
                <div className="space-y-6">
                <h3 className="text-3xl font-bold text-white mb-4">
                    Excellence Framework
                </h3>
                
                <p className="text-gray-400 text-lg leading-relaxed mb-6">
                    ISF Athena proudly meets and exceeds the rigorous NAAC standards for academic excellence, institutional governance, and educational quality. Our systematic approach ensures continuous improvement across all dimensions of learning.
                </p>

                {/* Bullet Points */}
                <ul className="space-y-4">
                    <li className="flex items-start gap-3">
                    <span className="text-orange-500 mt-1">•</span>
                    <span className="text-gray-300">Comprehensive quality benchmarking systems</span>
                    </li>
                    <li className="flex items-start gap-3">
                    <span className="text-orange-500 mt-1">•</span>
                    <span className="text-gray-300">Data-driven assessment protocols</span>
                    </li>
                    <li className="flex items-start gap-3">
                    <span className="text-orange-500 mt-1">•</span>
                    <span className="text-gray-300">Active stakeholder engagement processes</span>
                    </li>
                    <li className="flex items-start gap-3">
                    <span className="text-orange-500 mt-1">•</span>
                    <span className="text-gray-300">Transparent governance structures</span>
                    </li>
                </ul>
                </div>

                {/* Personalized Adaptive Learning */}
                <div className="space-y-6">
                <h3 className="text-3xl font-bold text-white mb-4">
                    Personalized Adaptive Learning
                </h3>
                
                <p className="text-gray-400 text-lg leading-relaxed">
                    The PAL framework is seamlessly embedded within our NAAC-aligned quality systems, ensuring student-centric progress tracking that adapts to individual learning needs while maintaining institutional excellence standards.
                </p>
                </div>

            </div>

            {/* Right Content - Image Only */}
            <div className="relative z-10">
                <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img 
                    src="/Assests/home_page_13.png" 
                    alt="Personalized Adaptive Learning" 
                    className="w-full h-full object-cover"
                />
                </div>
                
                {/* Floating glow effects */}
                <div className="absolute top-0 right-0 w-40 h-40 bg-purple-500/20 rounded-full blur-3xl animate-pulse" />
                <div className="absolute bottom-0 left-0 w-40 h-40 bg-blue-500/20 rounded-full blur-3xl animate-pulse" style={{animationDelay: '1.5s'}} />
            </div>

            </div>
        </div>
        </section>

        {/* Chapter 2 - STEM Education Section */}
        <section className="relative">
            <div className="absolute inset-0 bg-gradient-to-b from-purple-900/40 via-purple-800/30 to-transparent"></div>
            
            <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-24 relative z-10">
                
                {/* Dark Transparent Blur Box - Chapter 2 */}
                <div className="bg-black/60 mt-6 backdrop-blur-md border-2 border-purple-500/50 rounded-3xl p-6 sm:p-12 lg:p-16 shadow-2xl shadow-purple-500/30">
                
                <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-6">
                    STEM Education at ISF Athena - Preparing Future Innovators
                </h3>
                
                <p className="text-gray-300 text-base sm:text-lg leading-relaxed max-w-5xl">
                    Our STEM programs represent the cutting edge of educational innovation, combining rigorous scientific inquiry with hands-on technological exploration. Through state-of-the-art facilities and world-class curricula, we're cultivating the next generation of innovators, problem-solvers, and global changemakers.
                </p>
                </div>

            </div>

            {/* Cyan/Turquoise Background Section - No Gap */}
            <div className="bg-cyan-500/80 backdrop-blur-md p-6 sm:p-12 lg:p-16 py-12 sm:py-16 mt-16">
                <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-24">
                
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-8 sm:mb-12">
                    STEM + Arts Integration: The Shuyuan Program
                </h2>

                {/* Three Columns */}
                <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-8 sm:gap-12">
                    <div className="space-y-4">
                        <h4 className="text-2xl font-bold text-black">
                            Multidisciplinary Excellence
                        </h4>
                        <p className="text-gray-900 leading-relaxed">
                            Shuyuan offers transformative courses blending Science, Technology, Engineering, Mathematics, and Arts—creating STEAM learners who think across boundaries and innovate at intersections.
                        </p>
                    </div>

                    {/* Signature Courses */}
                    <div className="space-y-4">
                        <h4 className="text-2xl font-bold text-black">
                            Signature Courses
                        </h4>
                        <p className="text-gray-900 leading-relaxed">
                            Cutting-edge curriculum includes Biotechnology, Nanotechnology, Environmental Science, and Computer Science—preparing students for careers that don't yet exist.
                        </p>
                    </div>

                    {/* Makerspace Innovation */}
                    <div className="space-y-4">
                        <h4 className="text-2xl font-bold text-black">
                            Makerspace Innovation
                        </h4>
                        <p className="text-gray-900 leading-relaxed">
                            Hands-on learning through advanced Makerspace labs featuring 3D printing, laser cutting, robotics workshops, and dedicated artificial intelligence laboratories.
                        </p>
                    </div>

                </div>
                </div>
            </div>
        </section>

        <section className="relative py-12 sm:py-20">

            <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-24 relative z-10">
                
                {/* Heading */}
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white text-center mb-8 sm:mb-12">
                    Strategic Focus on Innovation and AI Mastery
                </h2>

                {/* Hexagonal Cards Image */}
                <div className="flex justify-center mb-12">
                    <img 
                        src="/Assests/isf_standard_image_design.png" 
                        alt="Strategic Focus Cards" 
                        className="w-full max-w-6xl h-auto"
                    />
                </div>

                {/* Three Columns - Descriptions */}
                <div className="grid md:grid-cols-3 gap-8">
                    
                    {/* Description 1 */}
                    <div className="text-center space-y-3">
                        <h4 className="text-xl font-bold text-white">AI-First Strategy</h4>
                        <p className="text-gray-300 leading-relaxed text-sm">
                            ISF's ambitious 2024-29 Strategic Plan places Artificial Intelligence education and STEM innovation at the absolute core of our institutional mission and student experience.
                        </p>
                    </div>

                    {/* Description 2 */}
                    <div className="text-center space-y-3">
                        <h4 className="text-xl font-bold text-white">Learning Analytics 2.0</h4>
                        <p className="text-gray-300 leading-relaxed text-sm">
                            Our sophisticated Learning Analytics System 2.0 platform supports highly personalized STEM learning pathways, adapting in real-time to student progress and potential.
                        </p>
                    </div>

                    {/* Description 3 */}
                    <div className="text-center space-y-3">
                        <h4 className="text-xl font-bold text-white">Global Collaboration</h4>
                        <p className="text-gray-300 leading-relaxed text-sm">
                            Strategic partnerships with prestigious global institutions enable experiential STEM programs, including groundbreaking Smart Cities projects with Columbia University.
                        </p>
                    </div>

                </div>

            </div>
        </section>

        <section className="relative py-12 sm:py-20">
            <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-24">
                
                {/* Top Heading */}
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-orange-500 mb-8">
                    Personalized Adaptive Learning (PAL) – Tailoring Education to Every Student
                </h2>

                {/* Quote with Orange Left Border */}
                <div className="border-l-4 border-orange-500 pl-6 mb-20">
                    <p className="text-gray-300 text-lg italic">
                        "Education is not one-size-fits-all. At ISF Athena, every student's learning journey is uniquely crafted to unlock their fullest potential through data-driven insights and adaptive methodologies."
                    </p>
                </div>

                {/* What is PAL Section */}
                <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-8 sm:mb-12">
                    What is PAL at ISF Athena?
                </h3>

                {/* Two Column Layout */}
                <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 items-center">
                    
                    {/* Left Side - Image */}
                    <div className="relative">
                        <img 
                            src="/Assests/home_page_14.png" 
                            alt="Personalized Adaptive Learning" 
                            className="w-full h-auto rounded-2xl shadow-2xl"
                        />
                    </div>

                    {/* Right Side - Three Cards */}
                    <div className="space-y-8">
                        
                        {/* Card 1 - Data-Driven Customization */}
                        <div className="space-y-3">
                            <div className="flex items-center gap-3">
                                <span className="text-orange-500 font-bold text-xl">01</span>
                                <div className="flex-1 h-px bg-orange-500"></div>
                            </div>
                            <h4 className="text-2xl font-bold text-white">
                                Data-Driven Customization
                            </h4>
                            <p className="text-gray-300 leading-relaxed">
                                PAL leverages sophisticated data analytics to dynamically adapt curriculum pace, content depth, and instructional strategies to each individual student's learning profile and progress patterns.
                            </p>
                        </div>

                        {/* Card 2 - Early Intervention */}
                        <div className="space-y-3">
                            <div className="flex items-center gap-3">
                                <span className="text-orange-500 font-bold text-xl">02</span>
                                <div className="flex-1 h-px bg-orange-500"></div>
                            </div>
                            <h4 className="text-2xl font-bold text-white">
                                Early Intervention
                            </h4>
                            <p className="text-gray-300 leading-relaxed">
                                Continuous assessment tools and comprehensive diagnostic tests identify learning gaps at their earliest stages, enabling timely, targeted support before challenges become obstacles.
                            </p>
                        </div>

                        {/* Card 3 - Academic Monitoring */}
                        <div className="space-y-3">
                            <div className="flex items-center gap-3">
                                <span className="text-orange-500 font-bold text-xl">03</span>
                                <div className="flex-1 h-px bg-orange-500"></div>
                            </div>
                            <h4 className="text-2xl font-bold text-white">
                                Academic Monitoring
                            </h4>
                            <p className="text-gray-300 leading-relaxed">
                                Seamless integration with the powerful SABI5® Academic Monitoring System ensures precise, targeted academic support delivered exactly when and where students need it most.
                            </p>
                        </div>

                    </div>

                </div>

            </div>
        </section>

        <section className="relative py-12 sm:py-20">
            <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-24">
                
                {/* Heading */}
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white text-center mb-10 sm:mb-16">
                    Benefits of PAL under NAAC Standards
                </h2>

                {/* Three Cards Grid */}
                <div className="grid md:grid-cols-3 gap-8">
                    
                    {/* Card 1 - Educational Equity */}
                    <div className="relative group h-full">
                        <div className="absolute -inset-0.5 bg-gradient-to-r from-cyan-500 via-blue-500 to-purple-500 rounded-2xl opacity-75 group-hover:opacity-100 transition duration-300 blur-sm"></div>
                        <div className="relative bg-black rounded-2xl p-8 h-full">
                            <h4 className="text-2xl font-bold text-white mb-4">
                                Educational Equity
                            </h4>
                            <p className="text-gray-400 leading-relaxed">
                                PAL significantly enhances educational equity by thoughtfully addressing the full spectrum of diverse learning styles, abilities, cognitive preferences, and developmental paces across our student population.
                            </p>
                        </div>
                    </div>

                    {/* Card 2 - Student Agency */}
                    <div className="relative group h-full">
                        <div className="absolute -inset-0.5 bg-gradient-to-r from-green-500 via-cyan-500 to-blue-500 rounded-2xl opacity-75 group-hover:opacity-100 transition duration-300 blur-sm"></div>
                        <div className="relative bg-black rounded-2xl p-8 h-full">
                            <h4 className="text-2xl font-bold text-white mb-4">
                                Student Agency
                            </h4>
                            <p className="text-gray-400 leading-relaxed">
                                Our framework actively promotes student agency and cultivates self-directed learning capabilities, empowering learners to take ownership of their educational journey with confidence.
                            </p>
                        </div>
                    </div>

                    {/* Card 3 - Faculty Empowerment */}
                    <div className="relative group h-full">
                        <div className="absolute -inset-0.5 bg-gradient-to-r from-purple-500 via-pink-500 to-cyan-500 rounded-2xl opacity-75 group-hover:opacity-100 transition duration-300 blur-sm"></div>
                        <div className="relative bg-black rounded-2xl p-8 h-full">
                            <h4 className="text-2xl font-bold text-white mb-4">
                                Faculty Empowerment
                            </h4>
                            <p className="text-gray-400 leading-relaxed">
                                Teachers receive actionable, real-time data insights that enable them to continuously refine teaching strategies, personalize instruction, and maximize learning outcomes for every student.
                            </p>
                        </div>
                    </div>

                </div>

            </div>
        </section>

      <section className="relative">
            <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-24">
                
                {/* Top Heading and Description */}
                <div className="text-center mb-10 sm:mb-16">
                    <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
                        ISF Athena's Holistic Educational Philosophy
                    </h2>
                    <p className="text-gray-300 text-base sm:text-lg leading-relaxed max-w-5xl mx-auto">
                        True education extends far beyond academics. At ISF Athena, we nurture the whole person—integrating rigorous intellectual development with deep character formation, cultural wisdom, and ethical leadership rooted in timeless values and contemporary global citizenship.
                    </p>
                </div>

            </div>

            {/* Purple/Dark Blue Background Section */}
            <div className="py-20 mt-12">
                <div className="max-w-7xl mx-auto px-8 lg:px-24">
                    
                    {/* Main Card with Border */}
                    <div className="relative group">
                        <div className="absolute -inset-0.5 bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 rounded-3xl opacity-50 blur-sm"></div>
                        <div className="relative bg-black rounded-3xl p-6 sm:p-12 lg:p-16">
                            
                            {/* Title */}
                            <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white text-center mb-10 sm:mb-16">
                                The Eight Virtues + One Framework
                            </h3>

                            {/* Flower Diagram with 6 Virtues - Grid Layout */}
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center mb-12">
                                
                                {/* Left Side Virtues */}
                                <div className="space-y-8 lg:text-right">
                                    {/* Respect */}
                                    <div>
                                        <h4 className="text-xl font-bold text-white mb-1">Respect</h4>
                                        <p className="text-gray-400 text-sm">Honoring others and ourselves</p>
                                    </div>
                                    {/* Harmony */}
                                    <div>
                                        <h4 className="text-xl font-bold text-white mb-1">Harmony</h4>
                                        <p className="text-gray-400 text-sm">Building peaceful relationships</p>
                                    </div>
                                    {/* Compassion */}
                                    <div>
                                        <h4 className="text-xl font-bold text-white mb-1">Compassion</h4>
                                        <p className="text-gray-400 text-sm">Showing empathy and kindness</p>
                                    </div>
                                </div>

                                {/* Center Image */}
                                <div className="flex justify-center">
                                    <img 
                                        src="/Assests/Eight Virtues.png" 
                                        alt="Eight Virtues Framework" 
                                        className="w-full max-w-sm h-auto"
                                    />
                                </div>

                                {/* Right Side Virtues */}
                                <div className="space-y-8 lg:text-left">
                                    {/* Responsibility */}
                                    <div>
                                        <h4 className="text-xl font-bold text-white mb-1">Responsibility</h4>
                                        <p className="text-gray-400 text-sm">Owning our choices and actions</p>
                                    </div>
                                    {/* Resilience */}
                                    <div>
                                        <h4 className="text-xl font-bold text-white mb-1">Resilience</h4>
                                        <p className="text-gray-400 text-sm">Persevering through challenges</p>
                                    </div>
                                    {/* Integrity */}
                                    <div>
                                        <h4 className="text-xl font-bold text-white mb-1">Integrity</h4>
                                        <p className="text-gray-400 text-sm">Acting with moral courage</p>
                                    </div>
                                </div>

                            </div>

                            {/* Bottom Description */}
                            <p className="text-gray-300 text-center leading-relaxed max-w-4xl mx-auto mt-12">
                                Core Chinese cultural values underpin comprehensive character development alongside academic rigor, perfectly aligning with NEP's visionary emphasis on value-based education and cultivating ethical, responsible global citizenship. This framework shapes global stewards who are innovative, deeply empathetic, and committed to social responsibility.
                            </p>

                        </div>
                    </div>

                </div>
            </div>
        </section>

        <section className="relative py-12 sm:py-20">
            <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-24">
                
                {/* Heading */}
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-10 sm:mb-16">
                    Bilingual and Bicultural Excellence
                </h2>

                {/* Three Cards Grid */}
                <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-6 sm:gap-8 mb-16 sm:mb-20">
                    
                    {/* Card 1 - Language Mastery */}
                    <div className="space-y-4">
                        <h4 className="text-2xl font-bold text-white">
                            Language Mastery
                        </h4>
                        <div className="rounded-2xl overflow-hidden">
                            <img 
                                src="/Assests/home_page_15.png" 
                                alt="Language Mastery" 
                                className="w-full h-64 object-cover"
                            />
                        </div>
                        <p className="text-gray-300 leading-relaxed">
                            Our rigorous Chinese-English bilingual curriculum culminates in the prestigious IB Diploma Programme, developing true linguistic fluency and cognitive flexibility.
                        </p>
                    </div>

                    {/* Card 2 - Cultural Literacy */}
                    <div className="space-y-4">
                        <h4 className="text-2xl font-bold text-white">
                            Cultural Literacy
                        </h4>
                        <div className="rounded-2xl overflow-hidden">
                            <img 
                                src="/Assests/home_page_16.png" 
                                alt="Cultural Literacy" 
                                className="w-full h-64 object-cover"
                            />
                        </div>
                        <p className="text-gray-300 leading-relaxed">
                            Comprehensive cultural literacy programs foster deep global awareness, authentic cross-cultural competence, and genuine respect for the world's rich diversity.
                        </p>
                    </div>

                    {/* Card 3 - Inclusive Support */}
                    <div className="space-y-4">
                        <h4 className="text-2xl font-bold text-white">
                            Inclusive Support
                        </h4>
                        <div className="rounded-2xl overflow-hidden">
                            <img 
                                src="/Assests/home_page_17.png" 
                                alt="Inclusive Support" 
                                className="w-full h-64 object-cover"
                            />
                        </div>
                        <p className="text-gray-300 leading-relaxed">
                            The Li Ren Center provides specialized education support, ensuring every student thrives through personalized interventions and inclusive practices.
                        </p>
                    </div>

                </div>

                {/* Chapter 5 Section - Two Column Layout */}
                <div className="grid lg:grid-cols-2 gap-8 items-center mt-12 sm:mt-20">
                    
                    {/* Left Side - Text */}
                    <div className="space-y-6">
                        <h4 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white">
                            Evidence of Excellence and Impact
                        </h4>
                        <p className="text-gray-300 text-base sm:text-lg leading-relaxed">
                            Excellence isn't just our aspiration—it's our demonstrated reality. Through measurable outcomes, prestigious recognition, and transformative student success stories, ISF Athena's impact resonates across classrooms, communities, and continents.
                        </p>
                    </div>

                    {/* Right Side - Trophy Image */}
                    <div className="flex justify-center lg:justify-end">
                        <div className="relative">
                            <img 
                                src="/Assests/home_page_18.png" 
                                alt="Excellence Trophy" 
                                className="w-full max-w-md h-auto max-h-96 object-contain rounded-2xl"
                            />
                        </div>
                    </div>

                </div>

            </div>
        </section>

        <section className="relative py-12 sm:py-20">
            {/* Background Image */}
            <div className="absolute inset-0">
                <img 
                    src="/Assests/home_page_15.png" 
                    alt="Academic Excellence Background" 
                    className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black/50"></div>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-24 relative z-10">
                
                {/* Heading */}
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white text-center mb-10 sm:mb-16">
                    Academic Outcomes and Global Recognition
                </h2>

                {/* Three Stats Grid */}
                <div className="grid sm:grid-cols-3 gap-6 sm:gap-8 mb-12">
                    
                    {/* Stat 1 - Top 5% */}
                    <div className="text-center space-y-4">
                        <h3 className="text-5xl sm:text-6xl lg:text-4xl font-bold text-white">
                            Top 5%
                        </h3>
                        <h4 className="text-xl sm:text-2xl font-bold text-white">
                            Global Rankings
                        </h4>
                        <p className="text-gray-200 leading-relaxed text-sm sm:text-base">
                            ISF Athena consistently ranks among the world's top 5% of schools for exceptional academic results and competitive university placements.
                        </p>
                    </div>

                    {/* Stat 2 - 100% */}
                    <div className="text-center space-y-4">
                        <h3 className="text-5xl sm:text-6xl lg:text-4xl font-bold text-white">
                            100%
                        </h3>
                        <h4 className="text-xl sm:text-2xl font-bold text-white">
                            Accreditation
                        </h4>
                        <p className="text-gray-200 leading-relaxed text-sm sm:text-base">
                            Fully accredited by Council of International Schools (CIS) and Western Association of Schools and Colleges (WASC).
                        </p>
                    </div>

                    {/* Stat 3 - 45+ */}
                    <div className="text-center space-y-4">
                        <h3 className="text-5xl sm:text-6xl lg:text-4xl font-bold text-white">
                            45+
                        </h3>
                        <h4 className="text-xl sm:text-2xl font-bold text-white">
                            Countries
                        </h4>
                        <p className="text-gray-200 leading-relaxed text-sm sm:text-base">
                            Alumni succeed in prestigious universities across 45+ countries, assuming leadership roles in diverse global industries.
                        </p>
                    </div>

                </div>

                {/* Bottom Quote Box */}
                <div className="bg-orange-500 backdrop-blur-sm border-l-4 border-white rounded-xl p-8 mt-12">
                    <div className="flex items-start gap-4">
                        <div className="text-amber-600 text-2xl mt-1">💼</div>
                        <p className="text-white text-lg leading-relaxed">
                            Our students don't just attend elite universities—they thrive there, bringing the critical thinking, cultural wisdom, and innovative mindset cultivated at ISF Athena to shape the next generation of global solutions.
                        </p>
                    </div>
                </div>

            </div>
        </section>

        <section className="relative py-12 sm:py-20">
            <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-24">
                
                {/* Heading */}
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white text-center mb-8 sm:mb-12">
                    Innovative Learning Environments and Facilities
                </h2>

                {/* Four Images Row */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                    <div className="rounded-2xl overflow-hidden">
                        <img 
                            src="/Assests/home_page_19.png" 
                            alt="AI Laboratory" 
                            className="w-full h-48 object-cover"
                        />
                    </div>
                    <div className="rounded-2xl overflow-hidden">
                        <img 
                            src="/Assests/home_page_20.png" 
                            alt="Campus Building" 
                            className="w-full h-48 object-cover"
                        />
                    </div>
                    <div className="rounded-2xl overflow-hidden">
                        <img 
                            src="/Assests/home_page_21.png" 
                            alt="Digital Platform" 
                            className="w-full h-48 object-cover"
                        />
                    </div>
                    <div className="rounded-2xl overflow-hidden">
                        <img 
                            src="/Assests/home_page_22.png" 
                            alt="Safe Campus" 
                            className="w-full h-48 object-cover"
                        />
                    </div>
                </div>

                {/* Three Cards Grid */}
                <div className="grid md:grid-cols-3 gap-6">
                    
                    {/* Card 1 - AI Laboratory */}
                    <div className="bg-orange-200/80 backdrop-blur-sm rounded-2xl p-8 border-2 border-orange-500">
                        <h4 className="text-2xl font-bold text-black mb-4">
                            AI Laboratory
                        </h4>
                        <p className="text-gray-900 leading-relaxed">
                            State-of-the-art Artificial Intelligence Laboratory and immersive Experiential Learning Center at our stunning Shek Pik campus, where theoretical knowledge meets practical innovation.
                        </p>
                    </div>

                    {/* Card 2 - Digital Platforms */}
                    <div className="bg-orange-200/80 backdrop-blur-sm rounded-2xl p-8 border-2 border-orange-500">
                        <h4 className="text-2xl font-bold text-black mb-4">
                            Digital Platforms
                        </h4>
                        <p className="text-gray-900 leading-relaxed">
                            Advanced Makerspaces and sophisticated digital learning platforms dramatically enhance both STEM exploration and creative expression across all disciplines.
                        </p>
                    </div>

                    {/* Card 3 - Safe Campus */}
                    <div className="bg-orange-200/80 backdrop-blur-sm rounded-2xl p-8 border-2 border-orange-500">
                        <h4 className="text-2xl font-bold text-black mb-4">
                            Safe Campus
                        </h4>
                        <p className="text-gray-900 leading-relaxed">
                            Secure, inclusive campus featuring comprehensive barrier-free access, robust safeguarding policies, and a nurturing environment where every student feels valued and protected.
                        </p>
                    </div>

                </div>

            </div>
        </section>

        <section className="relative">
            
            {/* Top Section - Black Background */}
            <div className="bg-black py-12 sm:py-20">
                <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-24">
                    <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white text-center mb-6">
                        Community Engagement and Future Vision
                    </h2>
                    <p className="text-gray-300 text-base sm:text-lg text-center leading-relaxed max-w-5xl mx-auto">
                        Education is inherently collaborative. ISF Athena's success stems from vibrant partnerships with parents, dedicated educators, and visionary global institutions—all united in our shared mission to nurture tomorrow's compassionate, capable leaders.
                    </p>
                </div>
            </div>

            {/* Bottom Section - Background Image */}
            <div className="relative py-12 sm:py-20">
                {/* Background Image */}
                <div className="absolute inset-0">
                    <img 
                        src="/Assests/home_page_20.png" 
                        alt="Collaboration Background" 
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black/70"></div>
                </div>

                <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-24 relative z-10">
                    
                    {/* Heading */}
                    <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white text-center mb-10 sm:mb-16">
                        Collaboration and Stakeholder Empowerment
                    </h3>

                    {/* Timeline with 4 Points */}
                    <div className="relative">
                        
                        {/* Horizontal Line */}
                        <div className="absolute top-12 left-0 right-0 h-0.5 bg-gray-500 hidden md:block"></div>

                        {/* Four Points Grid */}
                        <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8">
                            
                            {/* Point 1 - Family Partnerships */}
                            <div className="text-center space-y-4">
                                <div className="flex flex-col items-center">
                                    <div className="w-12 h-12 rounded-full border-2 border-orange-500 bg-black flex items-center justify-center text-white font-bold text-xl mb-4 relative z-10">
                                        1
                                    </div>
                                    <div className="w-0.5 h-8 bg-orange-500"></div>
                                </div>
                                <h4 className="text-xl font-bold text-white">
                                    Family Partnerships
                                </h4>
                                <p className="text-gray-300 text-sm leading-relaxed">
                                    ISF actively fosters meaningful partnerships with parents and families, recognizing them as essential partners in the educational journey and student success.
                                </p>
                            </div>

                            {/* Point 2 - Global Networks */}
                            <div className="text-center space-y-4">
                                <div className="flex flex-col items-center">
                                    <div className="w-12 h-12 rounded-full border-2 border-orange-500 bg-black flex items-center justify-center text-white font-bold text-xl mb-4 relative z-10">
                                        2
                                    </div>
                                    <div className="w-0.5 h-8 bg-orange-500"></div>
                                </div>
                                <h4 className="text-xl font-bold text-white">
                                    Global Networks
                                </h4>
                                <p className="text-gray-300 text-sm leading-relaxed">
                                    Strategic collaborations with prestigious global institutions and educational leaders amplify our impact and expand opportunities for our students.
                                </p>
                            </div>

                            {/* Point 3 - Educator Excellence */}
                            <div className="text-center space-y-4">
                                <div className="flex flex-col items-center">
                                    <div className="w-12 h-12 rounded-full border-2 border-orange-500 bg-black flex items-center justify-center text-white font-bold text-xl mb-4 relative z-10">
                                        3
                                    </div>
                                    <div className="w-0.5 h-8 bg-orange-500"></div>
                                </div>
                                <h4 className="text-xl font-bold text-white">
                                    Educator Excellence
                                </h4>
                                <p className="text-gray-300 text-sm leading-relaxed">
                                    Unwavering commitment to continuous educator training and professional development ensures our teaching community remains at the forefront of pedagogical innovation.
                                </p>
                            </div>

                            {/* Point 4 - Financial Access */}
                            <div className="text-center space-y-4">
                                <div className="flex flex-col items-center">
                                    <div className="w-12 h-12 rounded-full border-2 border-orange-500 bg-black flex items-center justify-center text-white font-bold text-xl mb-4 relative z-10">
                                        4
                                    </div>
                                    <div className="w-0.5 h-8 bg-orange-500"></div>
                                </div>
                                <h4 className="text-xl font-bold text-white">
                                    Financial Access
                                </h4>
                                <p className="text-gray-300 text-sm leading-relaxed">
                                    Comprehensive financial assistance programs and strategic seeding of innovative projects ensure excellence remains accessible to deserving students from all backgrounds.
                                </p>
                            </div>

                        </div>

                    </div>

                </div>
            </div>
        </section>

        <section className="relative py-12 sm:py-20 bg-indigo-950">
            <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-24">
                
                {/* Heading */}
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white text-center mb-10 sm:mb-16">
                    Preparing Students for the Future of Work
                </h2>

                {/* Two Column Layout */}
                <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 items-start">
                    
                    {/* Left Side - Content */}
                    <div className="space-y-6">
                        
                        {/* Subheading */}
                        <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold text-white mb-4">
                            Future-Ready Skills
                        </h3>

                        {/* Paragraph 1 */}
                        <p className="text-gray-400 leading-relaxed">
                            Our forward-looking curriculum anticipates evolving global challenges with strategic emphasis on artificial intelligence literacy, creative problem-solving, ethical leadership, and adaptive thinking—the essential competencies for navigating an uncertain future.
                        </p>

                        {/* Paragraph 2 */}
                        <p className="text-gray-400 leading-relaxed">
                            The powerful combination of PAL and comprehensive STEM programs equips students with both technical expertise and human-centered skills for thriving in a rapidly transforming world characterized by technological disruption and unprecedented opportunities.
                        </p>

                        {/* Bullet Points */}
                        <ul className="space-y-3 mt-6">
                            <li className="flex items-start gap-3 text-gray-300">
                                <span className="text-cyan-400 mt-1">•</span>
                                <span>Computational thinking and AI fluency</span>
                            </li>
                            <li className="flex items-start gap-3 text-gray-300">
                                <span className="text-cyan-400 mt-1">•</span>
                                <span>Creative innovation and design thinking</span>
                            </li>
                            <li className="flex items-start gap-3 text-gray-300">
                                <span className="text-cyan-400 mt-1">•</span>
                                <span>Intercultural collaboration skills</span>
                            </li>
                            <li className="flex items-start gap-3 text-gray-300">
                                <span className="text-cyan-400 mt-1">•</span>
                                <span>Ethical decision-making frameworks</span>
                            </li>
                        </ul>

                    </div>

                    {/* Right Side - Image Card */}
                    <div className="relative">
                        <div className="relative group">
                            <div className="absolute -inset-0.5 bg-gradient-to-b from-cyan-400 via-blue-500 to-purple-500 rounded-3xl opacity-75 blur-sm"></div>
                            <div className="relative bg-white/10 backdrop-blur-sm rounded-3xl overflow-hidden border-2 border-white/20">
                                <img 
                                    src="/Assests/home_page_60.png" 
                                    alt="Students Learning" 
                                    className="w-full h-auto"
                                />
                                <div className="p-6 bg-indigo-900/80 backdrop-blur-sm">
                                    <p className="text-white text-center leading-relaxed">
                                        Our vision: nurture leaders who innovate with integrity and cultural wisdom.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                </div>

            </div>
        </section>

        <section className="relative py-12 sm:py-20 bg-black">
            <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-24">
                
                {/* Heading */}
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-8 sm:mb-12">
                    Join ISF Athena: Where Tradition Meets Innovation for Tomorrow's Leaders
                </h2>

                {/* Three Cards Grid */}
                <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-6 mb-12">
                    
                    {/* Card 1 - World-Class Standards */}
                    <div className="bg-black border-2 border-gray-700 rounded-2xl p-8 hover:border-blue-400 transition-colors duration-300">
                        <div className="w-14 h-14 rounded-full bg-blue-400/20 flex items-center justify-center mb-6">
                            <svg className="w-8 h-8 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                            </svg>
                        </div>
                        <h4 className="text-2xl font-bold text-white mb-4">
                            World-Class Standards
                        </h4>
                        <p className="text-gray-400 leading-relaxed">
                            Experience exceptional education fully aligned with NEP 2020 and rigorous NAAC standards—where national vision meets international excellence in every classroom.
                        </p>
                    </div>

                    {/* Card 2 - Personalized STEM */}
                    <div className="bg-black border-2 border-gray-700 rounded-2xl p-8 hover:border-cyan-400 transition-colors duration-300">
                        <div className="w-14 h-14 rounded-full bg-cyan-400/20 flex items-center justify-center mb-6">
                            <svg className="w-8 h-8 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                            </svg>
                        </div>
                        <h4 className="text-2xl font-bold text-white mb-4">
                            Personalized STEM
                        </h4>
                        <p className="text-gray-400 leading-relaxed">
                            Empower your child with truly personalized adaptive learning and cutting-edge STEM opportunities that ignite curiosity and unlock unprecedented potential.
                        </p>
                    </div>

                    {/* Card 3 - Vibrant Community */}
                    <div className="bg-black border-2 border-gray-700 rounded-2xl p-8 hover:border-purple-400 transition-colors duration-300">
                        <div className="w-14 h-14 rounded-full bg-purple-400/20 flex items-center justify-center mb-6">
                            <svg className="w-8 h-8 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                            </svg>
                        </div>
                        <h4 className="text-2xl font-bold text-white mb-4">
                            Vibrant Community
                        </h4>
                        <p className="text-gray-400 leading-relaxed">
                            Become part of an inspiring, diverse community passionately dedicated to excellence, educational equity, and developing compassionate global stewards.
                        </p>
                    </div>

                </div>

                {/* Horizontal Line */}
                <div className="w-full h-px bg-gradient-to-r from-transparent via-gray-600 to-transparent mb-8"></div>

                {/* Bottom Text */}
                <p className="text-gray-300 text-lg mb-8 max-w-4xl">
                    The future belongs to those who prepare for it today.<b> At ISF Athena, we're not just preparing students for the future — we're empowering them to create it.</b>
                </p>

                {/* CTA Buttons */}
                <div className="flex flex-wrap gap-4">
                    <button className="bg-orange-500 hover:bg-orange-600 text-black font-bold px-8 py-3 rounded-lg transition-colors duration-300">
                        Apply Now
                    </button>
                    <button className="bg-transparent border-2 border-orange-500 hover:bg-orange-500/10 text-orange-500 font-bold px-8 py-3 rounded-lg transition-colors duration-300">
                        Book Campus Tour
                    </button>
                </div>

            </div>
        </section>

    </div>
  );
}

export default Standard;